/**
 * The confirmation before a deletion that cascades.
 *
 * Deleting a company takes its depots and their stock with it, which is
 * not what the word "supprimer" says on its own. So the dialog lists what
 * will go before anything does, and the toast afterwards says what went —
 * from the server's own count, not from this list, which was read before.
 *
 * Shared by the structure tabs and the families screen: one way to delete in
 * this feature, with one set of words for it.
 */

import { useTranslation } from "react-i18next";
import { useAction } from "@hardauto/core-data";
import { Alert, Button, Dialog, useToast } from "@hardauto/core-ui";

import type { Deletion } from "../../api";
import { NAMESPACE } from "../../i18n";

export interface DeleteTarget {
  /** Shown in the title: « Supprimer la société CPIT ? ». */
  title: string;
  /** What goes with it, one line each. Empty when nothing does. */
  consequences: readonly string[];
  run: () => Promise<Deletion>;
}

export interface DeleteDialogProps {
  target: DeleteTarget | null;
  onClose: () => void;
  onDeleted: () => void;
}

export function DeleteDialog({ target, onClose, onDeleted }: DeleteDialogProps) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();

  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      title={target?.title ?? ""}
      closeLabel={t("structure.cancel")}
      footer={
        <>
          <Button onClick={onClose}>{t("structure.cancel")}</Button>
          <Button
            variant="danger"
            busy={action.isBusy("delete")}
            busyLabel={t("deletion.busy")}
            onClick={() => {
              if (!target) return;
              void action.run("delete", async () => {
                try {
                  const outcome = await target.run();
                  toast.show(deletionSummary(t, outcome), { tone: "success" });
                  onDeleted();
                  onClose();
                } catch {
                  toast.show(t("deletion.failed"), { tone: "danger" });
                }
              });
            }}
          >
            {t("deletion.confirm")}
          </Button>
        </>
      }
    >
      <Alert tone="warning">{t("deletion.irreversible")}</Alert>
      {target && target.consequences.length > 0 ? (
        <>
          <p className="hardauto-deletion__lead">{t("deletion.alsoGoes")}</p>
          <ul className="hardauto-deletion__list">
            {target.consequences.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      ) : null}
      <p className="hardauto-deletion__hint">{t("deletion.hideInstead")}</p>
    </Dialog>
  );
}

type Translate = ReturnType<typeof useTranslation>["t"];

/** « CPIT supprimée : 2 dépôts, 140 lignes de stock. » — only the non-zero counts. */
function deletionSummary(t: Translate, outcome: Deletion): string {
  const parts = [
    outcome.depots > 0 ? t("deletion.count.depots", { count: outcome.depots }) : null,
    outcome.stock_lines > 0 ? t("deletion.count.stock", { count: outcome.stock_lines }) : null,
    outcome.price_rules > 0 ? t("deletion.count.rules", { count: outcome.price_rules }) : null,
    outcome.perimeter_rows > 0
      ? t("deletion.count.perimeter", { count: outcome.perimeter_rows })
      : null,
    outcome.articles_detached > 0
      ? t("deletion.count.detached", { count: outcome.articles_detached })
      : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0
    ? t("deletion.doneWith", { code: outcome.code, details: parts.join(", ") })
    : t("deletion.done", { code: outcome.code });
}
